import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import "./i18n"; // Initialize i18n

import HomePage from "./Components/HomePage/HomePage";
import LoginForm from "./Components/LoginForm/LoginForm";
import RegisterForm from "./Components/RegisterForm/RegisterForm";
import SSOCallbackPage from "./Components/SSO/SSOCallbackPage";
import Accueil from "./Components/Accueil/Accueil";
import Dashboard from './Components/Dashboard/dashboard';
import DemoForm from './Components/Accueil/DemoForm';
import DemoPage from './Components/Demo/DemoPage';
import CanvasWrapper from "./Components/canvas_drag_N_DROP/CanvasWrapper";
import Blog from "./Components/Accueil/Blog";
import { DiagramSettingsProvider } from "./Components/canvas_drag_N_DROP/DiagramSettingsContext";
import TestAPI from "./Components/TestAPI";
import AzureOverview from "./Components/AzureOverview";
import TestAzureResources from "./Components/TestAzureResources";
import ProjectsPage from "./Components/Projects/ProjectsPage";
import ProjectDetailPage from "./Components/Projects/ProjectDetailPage";
import CloudServicesPage from "./Components/CloudServices/CloudServicesPage";
import GettingStartedPage from "./Components/GettingStarted/GettingStartedPage";
import SettingsPage from "./Components/Settings/SettingsPage";
import TemplatesPage from "./Components/Templates/TemplatesPage";
import FinOpsPage from "./Components/FinOps/FinOpsPage";
import MonitoringPage from "./Components/Monitoring/MonitoringPage";
import ExecutionsPage from "./Components/Executions/ExecutionsPage";
import ExecutionDetailsPage from "./Components/Executions/ExecutionDetailsPage";
import NewExecutionPage from "./Components/Executions/NewExecutionPage";
import EnvironmentsPage from "./Components/Environments/EnvironmentsPage";
import EnvironmentVariablesPage from "./Components/Environments/EnvironmentVariablesPage";
import { AuthProvider } from "./context/AuthContext";
import { WhiteLabelProvider } from "./context/WhiteLabelContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import { ArchitectureTabsProvider } from "./context/ArchitectureTabsContext";
import { ToastProvider } from "./context/ToastContext";
import { ToastProvider as NotificationToastProvider } from "./Components/Notifications";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import ProtectedRoute from "./Components/ProtectedRoute";
import AdminLogin from "./Components/Admin/AdminLogin";
import AdminLayout from "./Components/Admin/AdminLayout";
import AdminDashboard from "./Components/Admin/AdminDashboard";
import AdminOrganizations from "./Components/Admin/AdminOrganizations";
import AdminUsers from "./Components/Admin/AdminUsers";
import AdminSubscriptions from "./Components/Admin/AdminSubscriptions";
import AdminPlans from "./Components/Admin/AdminPlans";
import AdminPromoCodes from "./Components/Admin/AdminPromoCodes";
import AdminSmtpConfig from "./Components/Admin/AdminSmtpConfig";
import AdminStripeConfig from "./Components/Admin/AdminStripeConfig";
import AdminAzureStorage from "./Components/Admin/AdminAzureStorage";
import AdminAuditLogs from "./Components/Admin/AdminAuditLogs";
import AdminInfrastructure from "./Components/Admin/AdminInfrastructure";
import AdminEnterpriseOnboarding from "./Components/Admin/AdminEnterpriseOnboarding";
import AdminEnterpriseLanes from "./Components/Admin/AdminEnterpriseLanes";
import AdminBusinessLanes from "./Components/Admin/AdminBusinessLanes";
import AdminPlatformConfig from "./Components/Admin/AdminPlatformConfig";
import AdminAIConfig from "./Components/Admin/AdminAIConfig";
import ProtectedAdminRoute from "./Components/Admin/ProtectedAdminRoute";
import AcceptInvitationPage from "./Components/AcceptInvitation/AcceptInvitationPage";
import FeatureBlockedListener from "./Components/common/FeatureBlockedListener";

function App() {
  return (
    <Router>
      <AdminAuthProvider>
      <NotificationToastProvider>
        <ToastProvider>
          <FeatureBlockedListener />
          <AuthProvider>
            <WhiteLabelProvider>
            <ArchitectureTabsProvider>
              <CurrencyProvider>
                <div className="min-h-screen bg-secondary text-white font-sans relative">
                  <Routes>
            <Route path="/" element={<Accueil />} />
            <Route path="/solutions" element={<HomePage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/sso/callback" element={<SSOCallbackPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <DemoPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/canvas"
              element={
                <ProtectedRoute>
                  <DiagramSettingsProvider>
                    <CanvasWrapper />
                  </DiagramSettingsProvider>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <ProjectDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <TemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/finops"
              element={
                <ProtectedRoute roles={['owner', 'admin']}>
                  <FinOpsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monitoring"
              element={
                <ProtectedRoute>
                  <MonitoringPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cloud-services"
              element={
                <ProtectedRoute>
                  <CloudServicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/getting-started"
              element={
                <ProtectedRoute>
                  <GettingStartedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/*"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Execution Routes - Phases 0-6 */}
            <Route
              path="/executions"
              element={
                <ProtectedRoute>
                  <ExecutionsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/executions/new"
              element={
                <ProtectedRoute>
                  <NewExecutionPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/executions/:runId"
              element={
                <ProtectedRoute>
                  <ExecutionDetailsPage />
                </ProtectedRoute>
              }
            />

            {/* Environments Routes */}
            <Route
              path="/environments"
              element={
                <ProtectedRoute>
                  <EnvironmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/environments/:environmentId/variables"
              element={
                <ProtectedRoute>
                  <EnvironmentVariablesPage />
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/demande-demo" element={<DemoForm />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/test" element={<TestAPI />} />
            <Route path="/azure" element={<AzureOverview />} />
            <Route path="/azure-test" element={<TestAzureResources />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/*"
              element={
                <ProtectedAdminRoute>
                  <AdminLayout />
                </ProtectedAdminRoute>
              }
            >
              <Route path="dashboard"      element={<AdminDashboard />} />
              <Route path="organizations"  element={<AdminOrganizations />} />
              <Route path="users"          element={<AdminUsers />} />
              <Route path="subscriptions"  element={<AdminSubscriptions />} />
              <Route path="plans"          element={<AdminPlans />} />
              <Route path="promo-codes"    element={<AdminPromoCodes />} />
              <Route path="smtp-config"    element={<AdminSmtpConfig />} />
              <Route path="stripe-config"  element={<AdminStripeConfig />} />
              <Route path="azure-storage"           element={<AdminAzureStorage />} />
              <Route path="audit-logs"              element={<AdminAuditLogs />} />
              <Route path="infrastructure"          element={<AdminInfrastructure />} />
              <Route path="enterprise-onboarding"   element={<AdminEnterpriseOnboarding />} />
              <Route path="enterprise-lanes"        element={<AdminEnterpriseLanes />} />
              <Route path="business-lanes"          element={<AdminBusinessLanes />} />
              <Route path="platform-config"         element={<AdminPlatformConfig />} />
              <Route path="ai-config"               element={<AdminAIConfig />} />
              <Route index element={<Navigate to="dashboard" replace />} />
            </Route>

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </CurrencyProvider>
            </ArchitectureTabsProvider>
            </WhiteLabelProvider>
          </AuthProvider>
        </ToastProvider>
      </NotificationToastProvider>
      </AdminAuthProvider>
    </Router>
  );
}

export default App;
